
// Debug state utilities for AI operations

// Create a state object to store the last AI interaction for debugging purposes
export const aiDebugState = {
  lastPrompt: "",
  lastResponse: "",
  responseTime: 0,
  timestamp: "",
  model: "",
};

// Create a state object to store the last enrichment interaction
export const enrichmentDebugState = {
  lastPrompt: "",
  lastResponse: "",
  responseTime: 0,
  timestamp: "",
  model: "",
  sources: [],
  provider: "", // Added property for tracking which provider was used
  completedAt: "", // Added property for tracking when the enrichment was completed
  failedProvider: "", // Added property for tracking failed provider
  failureReason: "", // Added property for tracking failure reason
  error: "", // Added property for tracking errors
};

// Debug utilities for tracking hook initialization and performance
export const hookDebugUtils = {
  // Track hook instances to detect duplicates
  hookInstances: new Map<string, number>(),
  
  // Register a new hook instance
  registerHook: (hookName: string, instanceId: string): void => {
    const key = `${hookName}_${instanceId}`;
    const count = hookDebugUtils.hookInstances.get(hookName) || 0;
    hookDebugUtils.hookInstances.set(hookName, count + 1);
    
    console.log(`[DEBUG] Hook initialized: ${key} (total instances: ${count + 1})`);
    
    if (count > 0) {
      console.warn(`[DEBUG] Multiple instances of ${hookName} detected! This may cause performance issues.`);
    }
  },
  
  // Unregister a hook instance
  unregisterHook: (hookName: string): void => {
    const count = hookDebugUtils.hookInstances.get(hookName) || 0;
    if (count > 0) {
      hookDebugUtils.hookInstances.set(hookName, count - 1);
      console.log(`[DEBUG] Hook unregistered: ${hookName} (remaining instances: ${count - 1})`);
    }
  },
  
  // Log performance metrics
  logPerformance: (operation: string, startTime: number): void => {
    const endTime = Date.now();
    const duration = endTime - startTime;
    console.log(`[PERFORMANCE] ${operation} completed in ${duration}ms`);
  },
  
  // Create a timestamp for logging
  timestamp: (): string => {
    return new Date().toISOString();
  },
  
  // Safe stringify to avoid React rendering issues
  safeStringify: (obj: any, fallback: string = "[Object]"): string => {
    if (obj === null || obj === undefined) return String(obj);
    if (typeof obj === "string") return obj;
    if (typeof obj === "number" || typeof obj === "boolean") return String(obj);
    
    try {
      return JSON.stringify(obj);
    } catch (err) {
      console.warn("[DEBUG] Failed to stringify object:", obj);
      return fallback;
    }
  },
  
  // Safe format for objects to prevent direct rendering in JSX
  safeObjectDisplay: (obj: any): string => {
    if (obj === null || obj === undefined) {
      return "";
    }
    
    if (typeof obj !== "object") {
      return String(obj);
    }
    
    try {
      return JSON.stringify(obj);
    } catch (err) {
      return "[Complex Object]";
    }
  },
  
  // Log React rendering errors
  logRenderingIssue: (component: string, props: any): void => {
    console.error(`[REACT ERROR] Potential rendering issue in ${component}:`);
    
    // Check for common issues
    if (props && typeof props === 'object') {
      Object.entries(props).forEach(([key, value]) => {
        if (value === undefined) {
          console.warn(`[REACT WARNING] Prop "${key}" is undefined`);
        } else if (value === null) {
          console.warn(`[REACT WARNING] Prop "${key}" is null`);
        } else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          console.warn(`[REACT WARNING] Prop "${key}" is an object that might be rendered directly:`, value);
        }
      });
    }
  }
};

// React error boundary logger
export const logReactError = (error: Error, errorInfo: any) => {
  console.error("[REACT ERROR BOUNDARY]", error);
  console.error("Component stack:", errorInfo?.componentStack || "No component stack available");
  
  // Try to decode minified React error
  const errorMessage = error.message || "";
  const errorCodeMatch = errorMessage.match(/Minified React error #(\d+)/);
  
  if (errorCodeMatch) {
    const errorCode = errorCodeMatch[1];
    console.error(`React error code: ${errorCode}`);
    console.error("Visit https://reactjs.org/docs/error-decoder.html?invariant=" + errorCode + " for more info");
  }
};
